/**
  * 应用主组件
  * 
  * 版本: 2.7.1
  * 变更记录:
  * - 2025-10-16: 性能优化 - 修复内存泄漏、移动纯函数到组件外部、优化常量定义
  * - 2025-10-16: 添加 Excel 文件版本号参数，解决浏览器缓存导致数据不更新的问题
  * - 2025-09-29: 新增 basicInfo.propertyFee（物业费）并参与租金合计；为 loadDefaultExcel 增加并发防护（useRef 标记 + finally 复位）；添加版本头与变更记录
  */
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import './App.css';

// 导入组件
import BasicInfo from './components/BasicInfo';
import OperatingCosts from './components/OperatingCosts';
import InitialCosts from './components/InitialCosts';
import CalculationResults from './components/CalculationResults';
import FinancialIndicators from './components/FinancialIndicators';
import BatchCalculator from './components/BatchCalculator';

// 导入数据模型和工具函数
import { loadExcelData, extractFilteredData, calculateResults, calculateOperatingCosts, FilteredData, BasicInfo as BasicInfoType, OperatingCosts as OperatingCostsType, InitialCosts as InitialCostsType, CalculationResults as CalculationResultsType } from './utils/dataModel';
import { maskSensitiveData } from './utils/consoleSecurity';

// 性能优化：缓存配置常量（移到组件外部）
const CACHE_CONFIG = {
  CACHE_KEY: 'excel-data-cache',
  CACHE_VERSION_KEY: 'excel-cache-version',
  CACHE_TIMESTAMP_KEY: 'excel-cache-timestamp',
  CURRENT_VERSION: '2.7.1',
  CACHE_DURATION: 24 * 60 * 60 * 1000, // 24小时缓存有效期
  EXCEL_FILE_VERSION: '2.7.1' // 修改 Excel 文件时递增此版本号
} as const;

function App() {
  // 访问统计 - 已移除未使用的 Hook 调用，统计功能由 index.html 中的脚本处理

  // 状态管理
  const [filteredData, setFilteredData] = useState<FilteredData>({
    provinces: [],
    cities: {},
    districts: {},
    cityTiers: {},
    districtTiers: {},
    businessAreas: [],
    operatingCostRatios: {},
    initialCostsData: undefined
  });
  
  // 基础信息状态
  const [basicInfo, setBasicInfo] = useState<BasicInfoType>({
    province: '',
    city: '',
    district: '',
    cityTier: '',
    businessArea: '',
    area: 0,
    estimatedRevenue: 0,
    fixedRent: 0,
    rentPercentage: 0,
    propertyFee: 0
  });
  
  // 月度运营费用状态
  const [operatingCosts, setOperatingCosts] = useState<OperatingCostsType>({
    waterElectricity: 0,
    otherStoreCosts: 0,
    brandManagementFee: 0,
    laborCosts: 0,
    rent: 0,
    total: 0,
    waterElectricityManual: null
  });
  
  // 建店成本状态
  const [initialCosts, setInitialCosts] = useState<InitialCostsType>({
    franchiseFee: 0,
    brandUsageFee: 0,
    designFee: 0,
    storeServiceFee: 0,
    equipmentCost: 0,
    decorationCost: 0,
    total: 0
  });
  
  // 测算结果状态
  const [calculationResults, setCalculationResults] = useState<CalculationResultsType>({
    actualEstimatedRevenue: 0,
    estimatedGrossMargin: 0,
    monthlyProfitNoDepreciation: 0,
    monthlyProfitWithDepreciation: 0,
    paybackPeriod: 0,
    rent36Month: 0,
    minRevenueFor36MonthPayback: 0
  });
  
  // 加载状态
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 添加一个状态来跟踪用户是否尝试过提交表单
  const [hasAttemptedSubmit, setHasAttemptedSubmit] = useState(false);
  
  // 禁用双击缩放
  React.useEffect(() => {
    // 禁用双击缩放
    let lastTouchEnd = 0;
    
    const handleTouchEnd = (event: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };
    
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
    
    // 清理函数，防止内存泄漏
    return () => {
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, []);
  
  // 并发防护标记：避免 React 18 严格模式导致的重复请求
  const isLoadingExcelRef = useRef(false);

  // 性能优化：使用useMemo缓存运营费用计算结果，避免不必要的重计算
  const calculatedOperatingCosts = useMemo(() => {
    return calculateOperatingCosts(
      basicInfo.estimatedRevenue,
      basicInfo.cityTier,
      filteredData.operatingCostRatios,
      filteredData.operatingCostLimits,
      filteredData.laborCostRules
    );
  }, [
    basicInfo.estimatedRevenue,
    basicInfo.cityTier,
    filteredData.operatingCostRatios,
    filteredData.operatingCostLimits,
    filteredData.laborCostRules
  ]);

  // 当计算结果变化时更新运营费用状态
  useEffect(() => {
    if (calculatedOperatingCosts.waterElectricity > 0 || calculatedOperatingCosts.otherStoreCosts > 0 || calculatedOperatingCosts.brandManagementFee > 0 || calculatedOperatingCosts.laborCosts > 0) {
      setOperatingCosts(prev => ({
        ...prev,
        waterElectricity: prev.waterElectricityManual ?? calculatedOperatingCosts.waterElectricity,
        otherStoreCosts: calculatedOperatingCosts.otherStoreCosts,
        brandManagementFee: calculatedOperatingCosts.brandManagementFee,
        laborCosts: calculatedOperatingCosts.laborCosts
      }));
    }
  }, [calculatedOperatingCosts]);
  
  // 加载默认Excel文件 - 性能优化：添加localStorage缓存机制
  const loadDefaultExcel = useCallback(async () => {
    if (isLoadingExcelRef.current) {
      return;
    }
    isLoadingExcelRef.current = true;
    try {
      setIsLoading(true);
      setError(null);

      // 检查缓存是否存在且有效
      const cachedData = localStorage.getItem(CACHE_CONFIG.CACHE_KEY);
      const cachedVersion = localStorage.getItem(CACHE_CONFIG.CACHE_VERSION_KEY);
      const cachedTimestamp = localStorage.getItem(CACHE_CONFIG.CACHE_TIMESTAMP_KEY);

      const now = Date.now();
      const isCacheValid = cachedData &&
                          cachedVersion === CACHE_CONFIG.CURRENT_VERSION &&
                          cachedTimestamp &&
                          (now - parseInt(cachedTimestamp)) < CACHE_CONFIG.CACHE_DURATION;

      if (isCacheValid) {
        try {
          // 使用缓存数据
          const data = JSON.parse(cachedData);
          const processed = extractFilteredData(data);
          setFilteredData(processed);

          console.log('从缓存加载Excel数据成功');
          setIsLoading(false);
          return;
        } catch (cacheError) {
          console.warn('缓存数据解析失败，将重新加载:', cacheError);
          // 清理损坏的缓存
          localStorage.removeItem(CACHE_CONFIG.CACHE_KEY);
          localStorage.removeItem(CACHE_CONFIG.CACHE_VERSION_KEY);
          localStorage.removeItem(CACHE_CONFIG.CACHE_TIMESTAMP_KEY);
        }
      }

      // 从网络加载Excel文件
      console.log('从网络加载Excel文件...');
      // 添加版本号参数以避免浏览器缓存问题
      const response = await fetch(`测算规则表.xlsx?v=${CACHE_CONFIG.EXCEL_FILE_VERSION}`);

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const arrayBuffer = await response.arrayBuffer();
      const data = await loadExcelData(arrayBuffer);

      // 缓存数据
      try {
        localStorage.setItem(CACHE_CONFIG.CACHE_KEY, JSON.stringify(data));
        localStorage.setItem(CACHE_CONFIG.CACHE_VERSION_KEY, CACHE_CONFIG.CURRENT_VERSION);
        localStorage.setItem(CACHE_CONFIG.CACHE_TIMESTAMP_KEY, now.toString());
        console.log('Excel数据已缓存到localStorage');
      } catch (storageError: unknown) {
        console.warn('无法缓存数据到localStorage:', storageError);
        // 如果存储空间不足，清理旧缓存后重试
        if (storageError instanceof Error && storageError.name === 'QuotaExceededError') {
          localStorage.removeItem(CACHE_CONFIG.CACHE_KEY);
          localStorage.removeItem(CACHE_CONFIG.CACHE_VERSION_KEY);
          localStorage.removeItem(CACHE_CONFIG.CACHE_TIMESTAMP_KEY);
        }
      }

      // 处理数据过滤和初始化下拉菜单
      const processed = extractFilteredData(data);
      setFilteredData(processed);

      console.log('网络加载Excel成功，提取的数据:', maskSensitiveData(processed));
      setIsLoading(false);

    } catch (err) {
      setError('加载测算规则表失败，请检查网络连接后重试');
      setIsLoading(false);
      console.error('Excel文件处理错误:', err);

      // 如果网络加载失败，尝试使用过期的缓存数据
      const fallbackCache = localStorage.getItem(CACHE_CONFIG.CACHE_KEY);
      if (fallbackCache) {
        try {
          console.log('尝试使用过期缓存数据...');
          const data = JSON.parse(fallbackCache);
          const processed = extractFilteredData(data);
          setFilteredData(processed);

          setError('使用离线数据，部分功能可能不是最新版本');
          console.log('使用过期缓存数据成功');
        } catch (fallbackError) {
          console.error('过期缓存数据也无法使用:', fallbackError);
        }
      }
    }
    finally {
      isLoadingExcelRef.current = false;
    }
  }, []); // 使用外部常量，不需要依赖数组

  // 尝试自动加载Excel文件
  useEffect(() => {
    loadDefaultExcel();
  }, [loadDefaultExcel]);
  
  // 性能优化：使用useMemo缓存表单验证结果
  const formValidation = useMemo(() => {
    const missingFields: string[] = [];

    // 检查必填字段
    if (!basicInfo.province) missingFields.push('省');
    if (!basicInfo.city) missingFields.push('市');
    if (!basicInfo.district) missingFields.push('区县');
    if (!basicInfo.businessArea) missingFields.push('商圈类型');
    if (basicInfo.area <= 0) missingFields.push('面积');
    if (basicInfo.estimatedRevenue <= 0) missingFields.push('预估月营业额');
    if (basicInfo.fixedRent <= 0 && basicInfo.rentPercentage <= 0) missingFields.push('固定租金或租金抽成');

    const isValid = missingFields.length === 0;

    return { isValid, missingFields };
  }, [
    basicInfo.province,
    basicInfo.city,
    basicInfo.district,
    basicInfo.businessArea,
    basicInfo.area,
    basicInfo.estimatedRevenue,
    basicInfo.fixedRent,
    basicInfo.rentPercentage
  ]);

  // 验证表单是否填写完整
  const isFormValid = useCallback((): boolean => {
    return formValidation.isValid;
  }, [formValidation.isValid]);

  // 获取缺失的必填字段列表
  const getMissingFields = useCallback((): string[] => {
    return formValidation.missingFields;
  }, [formValidation.missingFields]);
  


  // 处理开始测算按钮点击 - 性能优化：使用useCallback避免重复创建函数
  const handleCalculate = useCallback(() => {
    try {
      // 标记用户已尝试提交表单
      setHasAttemptedSubmit(true);

      // 检查表单是否填写完整
      if (!formValidation.isValid) {
        setError(`请填写以下必填信息：${formValidation.missingFields.join('、')}`);
        return;
      }

      // 清除错误信息
      setError(null);

      // 直接计算结果，不依赖hasAttemptedSubmit状态
      try {
        const results = calculateResults(basicInfo, operatingCosts, initialCosts, filteredData);

        // 检查计算结果有效性
        if (results.paybackPeriod <= 0 || results.minRevenueFor36MonthPayback <= 0) {
          console.warn('计算结果无效，请检查输入数据的合理性');
          setError('计算结果异常，请检查输入数据的合理性');
          return;
        }

        setCalculationResults(results);
      } catch (calcErr) {
        console.error('计算过程中出现错误:', calcErr);
        setError('计算过程中出现错误，请检查输入数据');
      }
    } catch (err) {
      setError('计算过程中出现错误，请检查输入数据');
      console.error('计算错误:', err);
    }
  }, [formValidation.isValid, formValidation.missingFields, basicInfo, operatingCosts, initialCosts, filteredData]);

  return (
    <div className="min-h-screen flex flex-col py-1 px-2">
      <div className="app-container animate-fade-in flex-1">
        {/* 标题和测算按钮在移动端垂直排列，在桌面端水平排列 */}
        <div className="flex flex-col sm:flex-row justify-between items-center mb-4">
          <h1 className="page-title m-0 mb-3 sm:mb-0">新店测算工具 v2.7.1</h1>
          <button
            onClick={handleCalculate}
            disabled={isLoading || (!isFormValid() && hasAttemptedSubmit)}
            className={`btn-primary ${isLoading ? 'btn-loading' : ''} text-white font-medium py-2 px-5 rounded-lg shadow-md hover:shadow-lg flex items-center justify-center gap-2 transition-all duration-300 text-sm w-full sm:w-auto ${!isFormValid() && hasAttemptedSubmit ? 'opacity-60 cursor-not-allowed' : ''}`}
            title={!isFormValid() && hasAttemptedSubmit ? `请填写必填信息：${getMissingFields().join('、')}` : ''}
          >
            {isLoading ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                计算中...
              </>
            ) : (
              "开始测算"
            )}
          </button>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 rounded-lg p-3 mb-4">
            <p className="flex items-center text-sm">
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
              {error}
            </p>
          </div>
        )}
        
        {/* 只在用户尝试提交表单且验证失败且没有显示错误时显示提示 */}
        {!isFormValid() && hasAttemptedSubmit && !error && (
          <div className="bg-yellow-50 border border-yellow-200 text-yellow-800 rounded-lg p-3 mb-4">
            <p className="flex items-center text-sm">
              <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
              </svg>
              请填写以下必填信息：{getMissingFields().join('、')}
            </p>
          </div>
        )}
        
        <div className="grid grid-cols-1 gap-2">
          <div className="animate-section">
            <BatchCalculator filteredData={filteredData} />
          </div>
          <div className="animate-section">
            <BasicInfo
              basicInfo={basicInfo}
              setBasicInfo={setBasicInfo}
              filteredData={filteredData}
              hasAttemptedSubmit={hasAttemptedSubmit}
            />
          </div>

          <div className="animate-section">
            <OperatingCosts
              operatingCosts={operatingCosts}
              setOperatingCosts={setOperatingCosts}
              basicInfo={basicInfo}
              filteredData={filteredData}
            />
          </div>

          <div className="animate-section">
            <InitialCosts
              initialCosts={initialCosts}
              setInitialCosts={setInitialCosts}
              filteredData={filteredData}
              basicInfo={basicInfo}
            />
          </div>

          {calculationResults.actualEstimatedRevenue > 0 && (
            <>
              <div className="animate-section">
                <CalculationResults results={calculationResults} />
              </div>
              <div className="animate-section">
                <FinancialIndicators
                  basicInfo={basicInfo}
                  operatingCosts={operatingCosts}
                  initialCosts={initialCosts}
                  results={calculationResults}
                />
              </div>
            </>
          )}
        </div>
      </div>

      {/* 页脚 */}
      <footer className="mt-auto py-4 text-center text-gray-400 text-xs">
        <p className="mb-0">© 2025 沈浪</p>
      </footer>

      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
        </div>
      )}
    </div>
  );
}

export default App;